import { useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch } from '../../redux/hooks';
import { logout } from '../../features/auth/authSlice';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useFilters } from '../../hooks/useFilters';
import { useShowCharts } from '../../hooks/useShowCharts';
import { BREAKPOINTS } from '../../constants';
import CustomSelect from '../CustomSelect';

const getUtcOffset = (tz) => {
  const fmt = new Intl.DateTimeFormat('en', { timeZone: tz, timeZoneName: 'shortOffset' });
  const parts = fmt.formatToParts(new Date());
  const raw = parts.find((p) => p.type === 'timeZoneName')?.value || 'GMT';
  return raw.replace('GMT', '') || '+00:00';
};

const offsetToMinutes = (offset) => {
  const m = offset.match(/^([+-])(\d{1,2})(?::(\d{2}))?$/);
  if (!m) return 0;
  const sign = m[1] === '-' ? -1 : 1;
  return sign * (parseInt(m[2], 10) * 60 + parseInt(m[3] || '0', 10));
};

/**
 * ~430 timezones. Decorate–sort–undecorate: resolve each offset exactly once
 * instead of building two Intl.DateTimeFormat instances per comparison
 * (which is thousands of them for a single sort).
 */
let timezoneOptionsCache = null;
const getTimezoneOptions = () => {
  timezoneOptionsCache ??= Intl.supportedValuesOf('timeZone')
    .map((tz) => {
      const offset = getUtcOffset(tz);
      return { value: tz, label: `(UTC${offset}) ${tz}`, offset, minutes: offsetToMinutes(offset) };
    })
    .sort((a, b) => a.minutes - b.minutes);
  return timezoneOptionsCache;
};

const buttonClass =
  'flex h-9 shrink-0 items-center gap-2 rounded-sm border px-2.5 text-[0.9rem] font-medium transition-colors md:px-3';

const Navbar = () => {
  const dispatch = useAppDispatch();
  const [filters, setFilters] = useFilters();
  const [showCharts, setShowCharts] = useShowCharts();

  const { isMobile } = useIsMobile();
  const [isVisible, setIsVisible] = useState(true);
  const prevScrollPosRef = useRef(0);

  // Intl.supportedValuesOf omits some valid zone names (notably "UTC"), which
  // would leave the control showing its placeholder for an applied filter.
  const timezoneOptions = useMemo(() => {
    const options = getTimezoneOptions();
    const current = filters.timezone;
    if (!current || options.some((o) => o.value === current)) return options;
    const offset = getUtcOffset(current);
    return [{ value: current, label: `(UTC${offset}) ${current}`, offset }, ...options];
  }, [filters.timezone]);

  const renderTimezoneLabel = (option) => {
    if (!option) return 'Timezone';

    if (isMobile) {
      const offset = option.offset ?? getUtcOffset(option.value);
      const simpleOffset = offset.replace(/:00$/, '').replace(/^([+-])0/, '$1');
      return (
        <span className="flex items-center gap-1">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          {simpleOffset === '+0' ? 'UTC' : simpleOffset}
        </span>
      );
    }

    const parts = option.value.split('/');
    return parts[parts.length - 1].replace(/_/g, ' ');
  };

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollPos = window.scrollY;
      if (window.innerWidth < BREAKPOINTS.TABLET) {
        setIsVisible(prevScrollPosRef.current > currentScrollPos || currentScrollPos < 10);
      } else {
        setIsVisible(true);
      }
      prevScrollPosRef.current = currentScrollPos;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav
      className={`fixed inset-x-0 top-0 z-[1000] flex h-16 items-center justify-between gap-2 border-b border-line bg-[rgba(18,18,18,0.96)] px-3 backdrop-blur-md transition-transform duration-300 md:gap-4 md:px-6 ${
        isVisible ? 'translate-y-0' : '-translate-y-full'
      }`}
    >
      <div className="inline-flex h-6 items-baseline text-lg leading-7 font-bold text-fg">
        OCTAGON
      </div>

      <div className="flex min-w-0 items-center gap-1.5 md:gap-2">
        <div className="w-[90px] shrink-0 md:w-[140px]">
          <CustomSelect
            value={filters.timezone || ''}
            onChange={(event) => setFilters({ timezone: event.target.value })}
            options={timezoneOptions}
            placeholder="Timezone"
            searchable={true}
            renderSelected={renderTimezoneLabel}
            controlClassName="px-2"
            menuClassName={
              isMobile
                ? 'fixed! top-14 right-3 left-auto! mt-0 w-[calc(100vw-24px)] max-w-[320px]'
                : 'w-[260px] right-0 left-auto!'
            }
          />
        </div>

        <button
          type="button"
          onClick={() => setShowCharts(!showCharts)}
          aria-pressed={showCharts}
          className={`${buttonClass} ${
            showCharts
              ? 'border-accent bg-accent-soft text-accent'
              : 'border-line bg-surface-raised text-fg'
          }`}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <line x1="18" y1="20" x2="18" y2="10" />
            <line x1="12" y1="20" x2="12" y2="4" />
            <line x1="6" y1="20" x2="6" y2="14" />
          </svg>
          <span className="max-md:hidden">Performance</span>
        </button>

        <button
          type="button"
          onClick={() => dispatch(logout())}
          aria-label="Log out"
          className={`${buttonClass} border-line bg-surface-raised text-fg-muted hover:text-fg`}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />
          </svg>
        </button>
      </div>
    </nav>
  );
};

export default Navbar;
